import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import TableColumn from "../../components/TableColumn/TableColumn";
import api from "../../services/api";
import { format } from "date-fns";
import profileDefault from "../../assets/images/profileDefault.png";
import "./PushNotificationDetail.scss";

const PushNotificationDetail = () => {
  const { groupKey } = useParams();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("notified_at");
  const [sortDirection, setSortDirection] = useState("desc");

  const records = 10;

  const formatDate = (timestamp) => {
    if (!timestamp) return "—";
    return format(new Date(timestamp), "MMM d, yyyy h:mm a");
  };

  const fetchDetail = useCallback(
    async (page) => {
      try {
        const response = await api.get(
          `/push-notification-admin/campaigns/${encodeURIComponent(groupKey)}`,
          {
            params: {
              page,
              records,
              sort_column: sortBy,
              sort: sortDirection,
              filter,
              search: searchTerm,
            },
          }
        );

        setCampaign(response.data.campaign);

        const items = response.data.data.map((r) => ({
          Photo: (
            <img
              className="rounded"
              style={{ height: 36, width: 36, objectFit: "cover" }}
              src={r.photo || profileDefault}
              alt="User"
            />
          ),
          Name: r.name,
          Email: r.email || "—",
          Phone: r.phone || "—",
          Status: (
            <span className={`status-badge ${r.read ? "opened" : "not-opened"}`}>
              {r.read ? "Opened" : "Not Opened"}
            </span>
          ),
          "Sent At": formatDate(r.notified_at),
          id: r.user_id,
        }));

        setRecipients(items);
        setTotalPages(response.data.total);
      } catch (error) {
        console.error("Error fetching campaign detail:", error);
      }
    },
    [groupKey, sortBy, sortDirection, filter, searchTerm]
  );

  useEffect(() => {
    fetchDetail(currentPage);
  }, [currentPage, sortDirection, filter, searchTerm, fetchDetail]);

  const fetchAllData = async () => {
    try {
      const response = await api.get(
        `/push-notification-admin/campaigns/${encodeURIComponent(groupKey)}`,
        {
          params: { records: 9999999999, filter },
        }
      );

      return response.data.data.map((r) => ({
        Name: r.name,
        Email: r.email || "—",
        Phone: r.phone || "—",
        Status: r.read ? "Opened" : "Not Opened",
        "Sent At": formatDate(r.notified_at),
        id: r.user_id,
      }));
    } catch (error) {
      console.error("Error exporting data:", error);
      return [];
    }
  };

  const columns = ["Photo", "Name", "Email", "Phone", "Status", "Sent At"];
  const sortableColumns = ["Name", "Status", "Sent At"];

  const handleChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const sortColumnsMapping = {
    Name: "name",
    Status: "read",
    "Sent At": "notified_at",
  };

  const handleSort = (columnName) => {
    const backendCol = sortColumnsMapping[columnName];
    if (sortBy === backendCol) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(backendCol);
      setSortDirection("asc");
    }
  };

  const actions = [
    {
      label: (
        <span style={{ fontSize: 13, color: "#4F30EA", fontWeight: 500 }}>
          View
        </span>
      ),
      onClick: (rowData) => {
        navigate(`/users/${rowData.id}`);
      },
    },
  ];

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="push-notification-detail">
            {/* Campaign summary header */}
            {campaign && (
              <div className="campaign-header">
                <button
                  className="back-btn"
                  onClick={() => navigate("/push-notifications")}
                >
                  Back to Notifications
                </button>

                <div className="campaign-info">
                  <h2>{campaign.title || "Push Notification"}</h2>
                  <p className="campaign-message">{campaign.message}</p>
                  <div className="campaign-stats">
                    <div className="stat">
                      <span className="stat-value">
                        {campaign.total_recipients}
                      </span>
                      <span className="stat-label">Recipients</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value opened">
                        {campaign.total_opened}
                      </span>
                      <span className="stat-label">Opened</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">
                        {campaign.total_recipients - campaign.total_opened}
                      </span>
                      <span className="stat-label">Not Opened</span>
                    </div>
                    <div className="stat">
                      <span
                        className={`stat-value ${
                          campaign.open_rate >= 50
                            ? "high"
                            : campaign.open_rate >= 20
                            ? "medium"
                            : "low"
                        }`}
                      >
                        {campaign.open_rate}%
                      </span>
                      <span className="stat-label">Open Rate</span>
                    </div>
                  </div>

                  {/* Filter tabs */}
                  <div className="filter-tabs">
                    {["all", "opened", "not_opened"].map((f) => (
                      <button
                        key={f}
                        className={`filter-tab ${filter === f ? "active" : ""}`}
                        onClick={() => {
                          setFilter(f);
                          setCurrentPage(1);
                        }}
                      >
                        {f === "all"
                          ? "All"
                          : f === "opened"
                          ? "Opened"
                          : "Not Opened"}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <TableColumn
              title=""
              hasHeader={false}
              columns={columns}
              data={recipients}
              sortableColumns={sortableColumns}
              hasExport={true}
              hasSearch={true}
              actions={actions}
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              records={records}
              inputSearchProps={{ value: searchTerm, onChange: handleChange }}
              fetchAllData={fetchAllData}
              onSort={handleSort}
            />
          </section>
        </article>
      </div>
    </main>
  );
};

export default PushNotificationDetail;
